import { Link } from 'react-router-dom';
import { Target, Heart, Zap, Users } from 'lucide-react';

export const AboutPage = () => {
  const values = [
    {
      icon: <Target size={32} />,
      title: 'Results-Driven',
      description: 'We focus on outcomes that matter — conversions, traffic, and revenue growth.',
    },
    {
      icon: <Heart size={32} />,
      title: 'Client-Focused',
      description: 'Your success is our success. We listen, understand, and deliver what you need.',
    },
    {
      icon: <Zap size={32} />,
      title: 'Fast Execution',
      description: 'We move quickly without compromising quality. Time to market matters.',
    },
    {
      icon: <Users size={32} />,
      title: 'Transparent Communication',
      description: 'No jargon, no surprises. Clear updates and honest timelines every step of the way.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-32" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6" data-testid="about-hero-heading">
            About Snappadeal
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-3xl mx-auto" data-testid="about-hero-description">
            We help ambitious brands grow online with high-converting websites that look great and perform even better.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-story-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-8" data-testid="about-story-heading">
            Our Story
          </h2>
          <div className="prose prose-lg text-slate-600 space-y-6">
            <p data-testid="about-story-p1">
              Snappadeal was founded on a simple belief: every business deserves a website that works as hard as they do. Too many businesses struggle with slow, expensive, or poorly designed websites that don't deliver results.
            </p>
            <p data-testid="about-story-p2">
              We set out to change that. Our team combines years of experience in design, development, and digital marketing to create websites that are beautiful, fast, and effective. We don't just build websites — we build tools that help businesses grow.
            </p>
            <p data-testid="about-story-p3">
              Based in Canada and working remotely with clients worldwide, we've helped startups launch, small businesses scale, and established brands refresh their online presence. Our approach is simple: understand your goals, design with purpose, build with precision, and deliver results that matter.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="about-values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-testid="about-values-heading">
              Why Choose Snappadeal
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-testid="about-values-description">
              What sets us apart from other agencies
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white border border-slate-200 p-8 rounded-2xl hover:border-[#10B981] transition-colors duration-300"
                data-testid={`value-card-${index}`}
              >
                <div className="text-[#10B981] mb-4">{value.icon}</div>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-3" data-testid={`value-title-${index}`}>
                  {value.title}
                </h3>
                <p className="text-slate-600" data-testid={`value-description-${index}`}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-testid="about-process-heading">
              How We Work
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-testid="about-process-description">
              Our proven process delivers results consistently
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/3194518/pexels-photo-3194518.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Team collaboration"
                className="rounded-2xl shadow-lg"
                data-testid="about-process-image"
              />
            </div>
            <div className="space-y-6">
              <div data-testid="about-process-step-1">
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                  1. Discovery & Strategy
                </h3>
                <p className="text-slate-600">
                  We start by understanding your business, target audience, and goals. This foundation ensures every design decision serves a purpose.
                </p>
              </div>
              <div data-testid="about-process-step-2">
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                  2. Design & Feedback
                </h3>
                <p className="text-slate-600">
                  Our designers create mockups that bring your vision to life. We iterate based on your feedback until you're completely satisfied.
                </p>
              </div>
              <div data-testid="about-process-step-3">
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                  3. Development & Testing
                </h3>
                <p className="text-slate-600">
                  Our developers build your site with clean code, fast loading speeds, and mobile-first design. We test thoroughly before launch.
                </p>
              </div>
              <div data-testid="about-process-step-4">
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                  4. Launch & Support
                </h3>
                <p className="text-slate-600">
                  We launch your site and provide ongoing support. Your success doesn't end at launch — we're here to help you grow.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="about-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6" data-testid="about-cta-heading">
            Ready to work with us?
          </h2>
          <p className="text-lg text-slate-600 mb-8" data-testid="about-cta-description">
            Let's discuss your project and see if we're a good fit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full px-8 py-4 font-bold shadow-lg shadow-green-500/20 hover:shadow-green-500/40 transition-all duration-300 hover:-translate-y-1 text-lg uppercase tracking-wide"
              data-testid="about-cta-primary"
            >
              Get Started
            </Link>
            <Link
              to="/work"
              className="bg-white text-slate-900 border border-slate-200 hover:border-slate-400 hover:bg-slate-50 rounded-full px-8 py-4 font-medium transition-all duration-300"
              data-testid="about-cta-secondary"
            >
              View Our Work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
